/**
 * b19-client: read the live Dex availability denominators from the Worker.
 *
 * GET /api/dex-availability returns the current National Dex total plus the
 * counts of species available in Pokemon GO (released and shiny-released). The
 * Worker always answers with a complete body: when its R2 cache is empty it
 * serves a `source: 'fallback'` body carrying the same baseline numbers the
 * client used to hardcode, so callers can trust a successful response without
 * branching on `source`.
 *
 * The query is global (the response is not filter-scoped), cached for 5 minutes
 * to match the Worker's `cache-control: public, max-age=300`, and never blocks
 * the first render: callers fall back to in-file placeholders until it resolves.
 */
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface DexAvailability {
  schemaVersion: 1;
  generatedAt: string;
  source: "pogoapi" | "fallback";
  nationalDexTotal: number;
  inGoTotal: number;
  shinyInGoTotal: number;
  upstreamHashes: { released: string; shiny: string };
  lastDriftWarning?: {
    delta: number;
    pogoapiShinyCount: number;
    leekduckShinyCount: number;
    at: string;
  };
}

const DEX_AVAILABILITY_URL = "/api/dex-availability";

export interface DexAvailabilityResult {
  data: DexAvailability | undefined;
  /** True only once a real response has been read from the endpoint. */
  isResolved: boolean;
}

export function useDexAvailability(): DexAvailabilityResult {
  const query = useQuery<DexAvailability>({
    queryKey: [DEX_AVAILABILITY_URL],
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiRequest("GET", DEX_AVAILABILITY_URL);
      return res.json();
    },
  });

  return { data: query.data, isResolved: query.isSuccess };
}
